// 7-col cycles table: entry row + exit row per closed cycle.
// Live PnL renders through React props (livePrice → CycleRows) — no direct DOM
// mutation, which previously raced React's reconciler on cycle close and threw
// NotFoundError: insertBefore ... not a child of this node.
// Flash animation on new entry rows via trade-new-buy/sell CSS class.
import { useState, useEffect } from "react";
import type { WindCycle, WindSession } from "@/types/api";
import { useLivePrice } from "@/providers/ws-provider";
import { classifyFee } from "@/lib/format";
import TimeAgo from "@/components/common/time-ago";
import {
  isCycleLive,
  isCycleOrphan,
  cycleBeInfo,
  cycleAiOrigin,
  estimateCyclePnl,
  REASON_TEXT,
  fmt,
} from "@/lib/wind-cycles-table-helpers";

interface WindCyclesTableProps {
  cycles: WindCycle[];
  session: WindSession;
}

// Persisted across renders — tracks cycles seen to detect new ones for flash.
const seenCycleIds = new Set<string>();
const recentCycleIds = new Set<string>();

export default function WindCyclesTable({ cycles, session }: WindCyclesTableProps) {
  const [hideClosedExits, setHideClosedExits] = useState(true);
  const livePrice = useLivePrice(session.symbol);

  // Detect new cycles and arm the flash animation.
  const [, forceFlash] = useState(0);
  useEffect(() => {
    let changed = false;
    for (const c of cycles) {
      if (!seenCycleIds.has(c.id)) {
        seenCycleIds.add(c.id);
        recentCycleIds.add(c.id);
        setTimeout(() => {
          recentCycleIds.delete(c.id);
          forceFlash((n) => n + 1);
        }, 5000);
        changed = true;
      }
    }
    if (changed) forceFlash((n) => n + 1);
  }, [cycles]);

  const filtered = hideClosedExits
    ? cycles.filter((c) => c.status !== "closed")
    : cycles;
  // Newest first: prefer closed_at (set when cycle terminates), fall back to
  // opened_at (active cycle), then created_at (pending). Don't rely on the
  // API's incoming order + .reverse() because re-fetches can return the list
  // with a different sort, which would scramble positions.
  const cycleTime = (c: (typeof filtered)[number]) =>
    new Date(c.closed_at ?? c.opened_at ?? c.created_at).getTime();
  const ordered = [...filtered].sort((a, b) => cycleTime(b) - cycleTime(a));

  return (
    <div>
      {/* Table header bar */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted" title="non-skipped / total">
          {cycles.filter(c => c.close_reason !== "ai-skip").length}/{cycles.length} cycle{cycles.length === 1 ? "" : "s"}
        </span>
        <button
          className={`inline-flex items-center h-7 px-3 text-xs font-medium rounded border transition-colors ${
            hideClosedExits
              ? "border-accent/60 text-accent bg-accent/10"
              : "border-border text-muted hover:text-text"
          }`}
          onClick={() => setHideClosedExits((v) => !v)}
        >
          Hide closed exits
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted border-b border-border">
              <th className="text-left py-1.5 pr-2 font-medium">Time</th>
              <th className="text-left py-1.5 pr-2 font-medium">Side</th>
              <th className="text-right py-1.5 pr-2 font-medium">Price</th>
              <th className="text-right py-1.5 pr-2 font-medium hidden md:table-cell">Fee</th>
              <th className="text-right py-1.5 pr-2 font-medium hidden md:table-cell">BE</th>
              <th className="text-left py-1.5 pr-2 font-medium">Status</th>
              <th className="text-right py-1.5 font-medium">PnL</th>
            </tr>
          </thead>
          {/* One <tbody> per cycle — a valid HTML pattern that gives React a
              stable keyed parent per cycle. When a cycle is removed (e.g. live
              → closed with hideClosedExits=true) React removes the whole
              <tbody> subtree instead of splicing individual <tr> siblings
              inside a shared <tbody>, which was causing insertBefore/
              removeChild NotFoundError on cycle transitions. */}
          {ordered.length === 0 ? (
            <tbody>
              <tr>
                <td
                  colSpan={7}
                  className="text-muted text-center py-8"
                >
                  {hideClosedExits
                    ? "No active cycles (toggle off to see history)"
                    : "No cycles yet"}
                </td>
              </tr>
            </tbody>
          ) : (
            ordered.map((c) => (
              <CycleRows
                key={c.id}
                cycle={c}
                session={session}
                isRecent={recentCycleIds.has(c.id)}
                livePrice={livePrice}
              />
            ))
          )}
        </table>
      </div>
    </div>
  );
}

// ── Per-cycle row(s) ──────────────────────────────────────────────────────────

interface CycleRowsProps {
  cycle: WindCycle;
  session: WindSession;
  isRecent: boolean;
  livePrice: number | null;
}

function CycleRows({ cycle: c, session, isRecent, livePrice }: CycleRowsProps) {
  const isLong = c.direction === "long";
  const live = isCycleLive(c);
  const orphan = isCycleOrphan(c);
  const sym = session.symbol;
  // Click-to-expand for ai-skipped rows on mobile. Desktop (sm+) shows the
  // reason inline by default; mobile users tap the row to reveal it.
  const [expanded, setExpanded] = useState(false);

  const flashCls = isRecent
    ? isLong
      ? "trade-new-buy"
      : "trade-new-sell"
    : "";

  // BE cell
  const beInfo = c.entry_price ? cycleBeInfo(c) : null;

  // Entry status label
  let statusNode: React.ReactNode;
  if (live) {
    // Surface live-cycle AI interventions inline (stage, force extension,
    // TP push, SL trail) so the user can see WHY this cycle's exits differ
    // from the session defaults without opening detail.
    const ext = c.force_extended_min ?? 0;
    const aiOrigin = cycleAiOrigin(c);
    // Stage tag: "BE-taker" / "BE-maker" / "force-exit". Prefix with "AI:"
    // when the stage came from an AI tp_tier override (vs time-based).
    let stageTag: string | null =
      c.stage === "be-taker-armed" ? "BE-T"
      : c.stage === "be-maker-armed" ? "BE-M"
      : c.stage === "force-exiting" ? "force-exit"
      : null;
    if (stageTag && aiOrigin.tpTierAi && c.stage !== "force-exiting") {
      stageTag = `AI:${stageTag}`;
    }
    // When AI pushed TP further from entry (tp_push_bp), there's no BE stage
    // but the current TP on OKX is past the original tp_pct target. Show
    // "AI:TP↑" so the user knows the exit was moved out.
    if (!stageTag && aiOrigin.tpPushed) stageTag = "AI:TP↑";
    // Independent SL-trail indicator — renders alongside stage tag since
    // they're orthogonal (SL trailed while stage=be-taker is possible).
    const slTag = aiOrigin.slTrailed ? "AI:SL" : null;
    // Tooltip aggregates all live AI touchpoints so hover explains them.
    const titleParts: string[] = [];
    if (ext > 0) titleParts.push(`AI extended force by ${ext}m`);
    if (aiOrigin.tpTierAi) titleParts.push("TP tier set by AI");
    if (aiOrigin.tpPushed) titleParts.push("TP pushed out by AI");
    if (aiOrigin.slTrailed) titleParts.push("SL trailed by AI");
    const statusTitle = titleParts.length > 0 ? titleParts.join(" · ") : undefined;
    statusNode = (
      <span className="inline-flex flex-col text-warn" title={statusTitle}>
        <span>{c.status === "pending" ? "opening" : "open"}</span>
        <span className="text-[0.65rem] text-dim flex gap-1 flex-wrap">
          {stageTag && (
            <span className={stageTag.startsWith("AI:") ? "text-accent" : undefined}>{stageTag}</span>
          )}
          {slTag && <span className="text-accent">{slTag}</span>}
          {ext > 0 && <span className="text-accent">⏱+{ext}m</span>}
        </span>
      </span>
    );
  } else if (orphan) {
    const aiSkipped = c.close_reason === "ai-skip";
    // Extract reasoning from the cached AI advice snapshot so the user can see
    // WHY the advisor declined without having to open the cycle detail.
    let aiReason: string | null = null;
    if (aiSkipped && c.ai_advice) {
      try {
        const parsed = JSON.parse(c.ai_advice) as { why?: string };
        if (typeof parsed.why === "string" && parsed.why.trim()) {
          aiReason = parsed.why.trim();
        }
      } catch {
        // Malformed JSON — ignore, fallback to generic label.
      }
    }
    const title = aiSkipped
      ? aiReason
        ? `AI declined: ${aiReason}`
        : "AI advisor declined this cycle; session retries after cooldown."
      : "Entry never filled (timeout / manual stop before fill)";
    statusNode = (
      <span className="inline-flex flex-col text-muted" title={title}>
        <span className="inline-flex items-center gap-1">
          {aiSkipped ? "ai skipped" : "skipped"}
          {/* Tap affordance — only meaningful on mobile where the inline
              reason is hidden; harmless on desktop (reason already visible). */}
          {aiReason && (
            <span className="sm:hidden text-[0.6rem] text-dim select-none" aria-hidden>
              {expanded ? "▾" : "▸"}
            </span>
          )}
        </span>
        {aiReason && (
          <span
            className={`text-[0.65rem] text-dim italic whitespace-normal break-words max-w-[260px] ${
              expanded ? "block" : "hidden sm:block"
            }`}
          >
            {aiReason}
          </span>
        )}
      </span>
    );
  } else if (c.exit_price) {
    statusNode = (
      <span className="text-muted">
        filled → {fmt(c.exit_price, sym)}
      </span>
    );
  } else {
    statusNode = <span className="text-muted">filled</span>;
  }

  // Live estimated PnL (initial render value; updated in-place by parent effect)
  let livePnlNode: React.ReactNode = <span className="text-dim">—</span>;
  if (live && c.entry_price && livePrice) {
    const est = estimateCyclePnl(c, livePrice);
    const estColor = est >= 0 ? "#22c55e" : "#ef4444";
    livePnlNode = (
      <span
        data-wind-cycle-pnl={c.id}
        style={{ color: estColor, fontStyle: "italic" }}
        title="Estimated PnL if closed at market"
      >
        {est >= 0 ? "+" : ""}
        {fmt(est, 4)}
      </span>
    );
  }

  // Fee cell helper
  function feeCell(fee: number | null, price: number | null, qty: number | null) {
    if (!fee || !price || !qty) return <span className="text-dim">—</span>;
    const cf = classifyFee(fee, price, qty);
    return (
      <span title={cf.title} className="text-muted">
        {fmt(fee, 4)}
      </span>
    );
  }

  // Newest-first within a single cycle: exit row (closed_at) is newer than
  // entry row (opened_at), so it renders first. The cycle-level sort in the
  // parent already orders cycles newest-first between each other.
  // Each cycle renders its own <tbody> — React treats it as a stable keyed
  // subtree, so transitions (live → closed, cycle filtered out) remove the
  // whole tbody cleanly instead of splicing <tr> siblings inside a shared
  // tbody. This is what was causing the persistent insertBefore/removeChild
  // NotFoundError on wind detail during cycle transitions.
  return (
    <tbody>
      <ExitRow cycle={c} session={session} visible={!live && !!c.exit_price} />

      {/* Entry row — ai-skipped rows are click-to-expand (mobile reveals
          the reason; desktop unchanged since reason is already inline). */}
      <tr
        className={`border-b border-border/40 ${flashCls} ${
          orphan && c.close_reason === "ai-skip" ? "cursor-pointer sm:cursor-default" : ""
        }`}
        onClick={() => {
          if (orphan && c.close_reason === "ai-skip") setExpanded(v => !v);
        }}
      >
        <td className="py-1.5 pr-2 text-muted">
          {/* Skipped cycles have opened_at=null; fall back to closed_at
              (when AI decided) or created_at so the Time column isn't empty.
              TimeAgo shows "Xm ago" with full datetime in the tooltip. */}
          <TimeAgo iso={c.opened_at ?? c.closed_at ?? c.created_at} />
        </td>
        <td className="py-1.5 pr-2">
          {/* Skipped cycles never opened a position — direction is
              meaningless, just show a dash. */}
          {orphan ? (
            <span className="text-dim">—</span>
          ) : isLong ? (
            <span className="text-success">↑ BUY</span>
          ) : (
            <span className="text-danger">↓ SELL</span>
          )}
        </td>
        <td className="py-1.5 pr-2 text-right font-mono">
          {c.entry_price ? fmt(c.entry_price, sym) : "-"}
        </td>
        <td className="py-1.5 pr-2 text-right hidden md:table-cell">
          {feeCell(c.entry_fee, c.entry_price, c.quantity)}
        </td>
        <td className="py-1.5 pr-2 text-right hidden md:table-cell">
          {beInfo ? (
            <span style={{ color: beInfo.color }} title={beInfo.title}>
              {fmt(beInfo.price, sym)}
            </span>
          ) : (
            <span className="text-dim">—</span>
          )}
        </td>
        <td className="py-1.5 pr-2">{statusNode}</td>
        <td className="py-1.5 text-right">{live ? livePnlNode : <span className="text-dim">—</span>}</td>
      </tr>
    </tbody>
  );
}

function ExitRow({
  cycle: c, session, visible,
}: { cycle: WindCycle; session: WindSession; visible: boolean }) {
  if (!visible) return null;
  const isLong = c.direction === "long";
  const sym = session.symbol;
  const pnlColor =
    c.pnl == null ? "#666" : c.pnl >= 0 ? "#22c55e" : "#ef4444";

  const reasonText = REASON_TEXT[c.close_reason ?? ""] ?? c.close_reason ?? "closed";
  const typeText = c.close_type ? ` · ${c.close_type}` : "";
  const tooltip = `${reasonText}${typeText}`;
  const ref = c.entry_price ? `← ${fmt(c.entry_price, sym)}` : "← ?";

  // For AI-triggered cuts, surface the AI's reasoning inline so the user
  // can see WHY the advisor pulled the plug without opening cycle detail.
  // Prefer `cut_why` (specific to cut decision); fall back to `why`.
  const aiCut = c.close_reason === "ai-cut";
  let aiCutReason: string | null = null;
  if (aiCut && c.ai_advice) {
    try {
      const parsed = JSON.parse(c.ai_advice) as { cut_why?: string; why?: string };
      const r = parsed.cut_why?.trim() || parsed.why?.trim();
      if (r) aiCutReason = r;
    } catch {
      // Malformed JSON — fall back to generic tooltip.
    }
  }

  function feeCell(fee: number | null, price: number | null, qty: number | null) {
    if (!fee || !price || !qty) return <span className="text-dim">—</span>;
    const cf = classifyFee(fee, price, qty);
    return (
      <span title={cf.title} className="text-muted">
        {fmt(fee, 4)}
      </span>
    );
  }

  return (
    <tr className="border-b border-border/20 bg-surface/40">
      <td className="py-1.5 pr-2 text-muted"><TimeAgo iso={c.closed_at} /></td>
      <td className="py-1.5 pr-2">
        {isLong ? (
          <span className="text-danger">↓ SELL</span>
        ) : (
          <span className="text-success">↑ BUY</span>
        )}
      </td>
      <td className="py-1.5 pr-2 text-right font-mono">
        {c.exit_price ? fmt(c.exit_price, sym) : "-"}
      </td>
      <td className="py-1.5 pr-2 text-right hidden md:table-cell">
        {feeCell(c.exit_fee, c.exit_price, c.quantity)}
      </td>
      <td className="py-1.5 pr-2 text-right hidden md:table-cell">
        <span className="text-dim">—</span>
      </td>
      <td className="py-1.5 pr-2">
        <span
          className="inline-flex flex-col"
          style={{ color: pnlColor, cursor: "default" }}
          title={aiCutReason ? `AI cut: ${aiCutReason}` : tooltip}
        >
          <span>{aiCut ? `ai cut ${ref}` : `closed ${ref}`}</span>
          {aiCutReason && (
            <span className="text-[0.65rem] text-dim italic max-w-[220px] truncate">
              {aiCutReason}
            </span>
          )}
        </span>
      </td>
      <td className="py-1.5 text-right">
        {c.pnl != null ? (
          <span style={{ color: pnlColor, fontWeight: 600 }}>
            {c.pnl >= 0 ? "+" : ""}
            {fmt(c.pnl, 4)}
          </span>
        ) : (
          <span className="text-dim">—</span>
        )}
      </td>
    </tr>
  );
}
